import React from "react";
import { Oval } from "react-loader-spinner";

import { OwesReportItem, Order } from "../../../../models";

import "./NewCardForm.scss";

declare global {
  interface Window {
    StaxJs: any;
  }
}

export interface NewCardFormProps {
  invoice: OwesReportItem;
  order?: Order;
  totalToCharge: number;
  applyServiceCharge: boolean;
  onPaymentSuccess: (result: any) => void;
  onPaymentError: (error: string) => void;
}

export const NewCardForm: React.FC<NewCardFormProps> = ({
  invoice,
  order,
  totalToCharge,
  applyServiceCharge,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const staxjsRef = React.useRef<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [cardFormComplete, setCardFormComplete] = React.useState(false);
  const [formValues, setFormValues] = React.useState({
    firstName: "",
    lastName: "",
    month: "",
    year: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });

  // Calculate line items for metadata
  const basePrice = applyServiceCharge ? totalToCharge / 1.02 : totalToCharge;
  const serviceChargeAmount = applyServiceCharge ? totalToCharge - basePrice : 0;

  React.useEffect(() => {
    // Check if StaxJs is already loaded
    if (window.StaxJs) {
      initializeStaxForm();
      return;
    }

    // Check if script is already in the document
    const existingScript = document.querySelector(
      'script[src="https://staxjs.staxpayments.com/stax.js"]'
    );

    if (existingScript) {
      // Script exists, wait for it to load
      existingScript.addEventListener("load", initializeStaxForm);
      return;
    }

    // Load Stax.js script
    const script = document.createElement("script");
    script.src = "https://staxjs.staxpayments.com/stax.js";
    script.async = true;
    script.onload = () => {
      initializeStaxForm();
    };
    script.onerror = () => {
      setIsLoading(false);
      onPaymentError("Failed to load payment form");
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup
      if (staxjsRef.current) {
        staxjsRef.current = null;
      }
    };
  }, []);

  const initializeStaxForm = () => {
    const token = process.env.REACT_APP_STAX_API_WEB_PAYMENTS_TOKEN;

    if (!token) {
      setIsLoading(false);
      onPaymentError("Payment configuration missing");
      return;
    }

    try {
      const staxjs = new window.StaxJs(token, {
        number: {
          id: "staxjs-number",
          placeholder: "0000 0000 0000 0000",
          style:
            "width: 100%; height: 20px; font-size: 14px; border: none; outline: none; padding: 0; background: transparent;",
          type: "text",
          format: "prettyFormat",
        },
        cvv: {
          id: "staxjs-cvv",
          placeholder: "CVV",
          style:
            "width: 100%; height: 20px; font-size: 14px; border: none; outline: none; padding: 0; background: transparent;",
          type: "text",
        },
      });

      staxjs.on("card_form_complete", () => {
        setCardFormComplete(true);
      });

      staxjs.on("card_form_uncomplete", () => {
        setCardFormComplete(false);
      });

      staxjs
        .showCardForm()
        .then(() => {
          setIsLoading(false);
        })
        .catch((err: any) => {
          console.error("Error initializing Stax form:", err);
          setIsLoading(false);
          onPaymentError("Failed to initialize payment form");
        });

      staxjsRef.current = staxjs;
    } catch (err) {
      console.error("Error creating StaxJs instance:", err);
      setIsLoading(false);
      onPaymentError("Failed to initialize payment form");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid = () => {
    return (
      cardFormComplete &&
      formValues.firstName.trim() !== "" &&
      formValues.lastName.trim() !== "" &&
      formValues.month.trim() !== "" &&
      formValues.year.trim() !== "" &&
      formValues.address.trim() !== "" &&
      formValues.city.trim() !== "" &&
      formValues.state.trim() !== "" &&
      formValues.zip.trim() !== ""
    );
  };

  const handleCharge = async () => {
    if (!staxjsRef.current || !isFormValid()) return;

    setIsProcessing(true);

    const lineItems = [
      {
        item: "Triple P Disposal",
        details: "",
        quantity: 1,
        price: basePrice,
      },
    ];

    if (applyServiceCharge) {
      lineItems.push({
        item: "Card Service Charge",
        details: "",
        quantity: 1,
        price: serviceChargeAmount,
      });
    }

    const extraDetails = {
      firstname: formValues.firstName,
      lastname: formValues.lastName,
      month: formValues.month,
      year: formValues.year,
      address_1: formValues.address,
      address_city: formValues.city,
      address_state: formValues.state,
      address_zip: formValues.zip,
      method: "card",
      validate: true,
      send_receipt: true,
      total: totalToCharge,
      company: order?.name || invoice.name,
      email: order?.email || "",
      phone: order?.phoneNumber || "",
      meta: {
        memo: invoice.orderId,
        reference: invoice.invoiceId,
        lineItems: lineItems,
      },
    };

    try {
      const result = await staxjsRef.current.pay(extraDetails);
      setIsProcessing(false);

      // Check if payment failed even though promise resolved
      if (result.payment_attempt_failed) {
        onPaymentError(result.payment_attempt_message || "Payment failed");
        return;
      }

      onPaymentSuccess(result);
    } catch (err: any) {
      setIsProcessing(false);
      const errorMessage =
        err.payment_attempt_message ||
        (typeof err === "object"
          ? err.message ||
            Object.keys(err)
              .map((k) => err[k].join?.(" ") || err[k])
              .join(" ")
          : String(err));
      onPaymentError(errorMessage);
    }
  };

  return (
    <div className="new-card-form-component">
      {isLoading && (
        <div className="loading-overlay">
          <Oval color="#007AFF" height={32} width={32} />
          <div className="loading-text">Loading payment form...</div>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label>First Name</label>
          <input
            type="text"
            name="firstName"
            placeholder="First name"
            value={formValues.firstName}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label>Last Name</label>
          <input
            type="text"
            name="lastName"
            placeholder="Last name"
            value={formValues.lastName}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Card Number</label>
        <div className="stax-field-wrapper">
          <div id="staxjs-number"></div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>CVV</label>
          <div className="stax-field-wrapper">
            <div id="staxjs-cvv"></div>
          </div>
        </div>
        <div className="form-group">
          <label>Expiration</label>
          <div className="expiration-inputs">
            <input
              type="text"
              name="month"
              placeholder="MM"
              maxLength={2}
              value={formValues.month}
              onChange={handleInputChange}
            />
            <span>/</span>
            <input
              type="text"
              name="year"
              placeholder="YYYY"
              maxLength={4}
              value={formValues.year}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label>Address</label>
        <input
          type="text"
          name="address"
          placeholder="Street address"
          value={formValues.address}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            name="city"
            placeholder="City"
            value={formValues.city}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group small">
          <label>State</label>
          <input
            type="text"
            name="state"
            placeholder="ST"
            maxLength={2}
            value={formValues.state}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group small">
          <label>Zip</label>
          <input
            type="text"
            name="zip"
            placeholder="ZIP"
            maxLength={5}
            value={formValues.zip}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <button
        className="charge-button"
        onClick={handleCharge}
        disabled={!isFormValid() || isProcessing}
      >
        {isProcessing ? (
          <Oval color="#fff" height={20} width={20} />
        ) : (
          `Charge Card - $${totalToCharge.toFixed(2)}`
        )}
      </button>
    </div>
  );
};
